import type { FC } from 'react';
import { useState } from 'react';
import { createPortal } from 'react-dom';
import { STICKY_NOTE_Z_INDEX } from '@/shared/lib/constants';
import type { StickyNote } from '@/shared/stores/sticky-note-store';
import { useStickyNoteStore } from '@/shared/stores/sticky-note-store';
import { CloseButton } from '@/shared/ui';
import { STICKY_NOTE_MIN_SIZE, STICKY_NOTE_TITLE_BAR_HEIGHT } from '../lib/sticky-note-constants';
import { useDragResize } from '../model/useDragResize';

type ResizeDirection = 'n' | 's' | 'e' | 'w' | 'ne' | 'nw' | 'se' | 'sw';

const RESIZE_HANDLES: { direction: ResizeDirection; className: string }[] = [
  { direction: 'n', className: 'top-0 left-2 right-2 h-1 cursor-ns-resize' },
  { direction: 's', className: 'bottom-0 left-2 right-2 h-1 cursor-ns-resize' },
  { direction: 'e', className: 'top-2 right-0 bottom-2 w-1 cursor-ew-resize' },
  { direction: 'w', className: 'top-2 left-0 bottom-2 w-1 cursor-ew-resize' },
  { direction: 'nw', className: 'top-0 left-0 h-2 w-2 cursor-nwse-resize' },
  { direction: 'ne', className: 'top-0 right-0 h-2 w-2 cursor-nesw-resize' },
  { direction: 'sw', className: 'bottom-0 left-0 h-2 w-2 cursor-nesw-resize' },
  { direction: 'se', className: 'bottom-0 right-0 h-2 w-2 cursor-nwse-resize' },
];

type StickyNoteItemProps = {
  note: StickyNote;
};

/**
 * 個別の付箋コンポーネント
 * ドラッグ移動、リサイズ、テキスト編集、削除が可能
 */
export const StickyNoteItem: FC<StickyNoteItemProps> = ({ note }) => {
  const updateNote = useStickyNoteStore((s) => s.updateNote);
  const removeNote = useStickyNoteStore((s) => s.removeNote);
  const [localContent, setLocalContent] = useState(note.content);

  const { position, size, isInteracting, handleDragStart, handleResizeStart } = useDragResize({
    initialPosition: { x: note.x, y: note.y },
    initialSize: { width: note.width, height: note.height },
    minSize: { ...STICKY_NOTE_MIN_SIZE },
    onChangeEnd: (pos, sz) => {
      updateNote(note.id, { x: pos.x, y: pos.y, width: sz.width, height: sz.height });
    },
  });

  const handleBlur = () => {
    if (localContent !== note.content) {
      updateNote(note.id, { content: localContent });
    }
  };

  return createPortal(
    <div
      style={{
        position: 'fixed',
        left: `${position.x}px`,
        top: `${position.y}px`,
        width: `${size.width}px`,
        height: `${size.height}px`,
        zIndex: STICKY_NOTE_Z_INDEX,
      }}
      className="flex flex-col overflow-hidden rounded border border-yellow-400 bg-yellow-100 shadow-lg"
    >
      {/* タイトルバー */}
      <div
        role="toolbar"
        onMouseDown={handleDragStart}
        style={{ height: `${STICKY_NOTE_TITLE_BAR_HEIGHT}px` }}
        className="flex shrink-0 cursor-move items-center justify-end border-yellow-300 border-b bg-yellow-200 px-1"
      >
        <CloseButton onClick={() => removeNote(note.id)} className="h-5 w-5" />
      </div>

      {/* テキストエリア */}
      <textarea
        value={localContent}
        onChange={(e) => setLocalContent(e.target.value)}
        onBlur={handleBlur}
        className="flex-1 resize-none border-none bg-transparent p-2 text-sm outline-none"
        style={{ pointerEvents: isInteracting ? 'none' : 'auto' }}
      />

      {/* リサイズハンドル */}
      {RESIZE_HANDLES.map(({ direction, className }) => (
        <button
          type="button"
          key={direction}
          aria-label={`${direction}方向にリサイズ`}
          onMouseDown={(e) => handleResizeStart(e, direction)}
          className={`absolute ${className}`}
        />
      ))}
    </div>,
    document.body,
  );
};
